import { useTranslations } from '@vocab/react';
import { Box, Text } from 'braid-design-system';
import { useDispatch } from 'react-redux';

import {
  AI_GENERATE_BUTTON_PRESSED,
  AI_GENERATED_LANGUAGE_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { getLanguageAI } from 'src/app/v1/dataAnalytics/utils/getLanguageAI';
import { actionUpdateAiLanguageSelected } from 'src/app/v1/state/modules/tealium/aiLanguageSelected';
import { useLanguagesForAI } from 'src/app/v1/views/hooks/useLanguagesForAI';
import type { LanguageAI } from 'src/types/languageAI';

import { hasOtherLanguageAIButton } from '../../utils/hasOtherLanguageAIButton';
import { AIContentGenerateButton } from '../AIContentGenerateButton';
import { LanguageSelectionAIContentGenerateButton } from '../LanguageSelectionAIContentGenerateButton';

import translations from './.vocab';

import * as styles from './index.css';

interface Props {
  onWriteWithAIClick: (locale?: LanguageAI) => void;
}

const Layout = ({ children }: { children: React.ReactNode }) => (
  <Box
    className={styles.layout}
    display="flex"
    alignItems={{ tablet: 'center', desktop: 'center' }}
    borderRadius="large"
    justifyContent="spaceBetween"
    padding="medium"
    position="relative"
    width="full"
  >
    {children}
  </Box>
);

export const WriteWithAICtaSection = ({ onWriteWithAIClick }: Props) => {
  const { t } = useTranslations(translations);
  const { getSite } = useConfig();
  const dispatch = useDispatch();
  const generationLanguages = useLanguagesForAI();

  const site = getSite();

  const handleOnClick = (language?: LanguageAI) => {
    if (language) {
      getTrackingAdapter.sendLinkEvent({
        eventName: AI_GENERATED_LANGUAGE_PRESSED,
        aiLanguageSelected: getLanguageAI(language),
      });
      dispatch(actionUpdateAiLanguageSelected(getLanguageAI(language)));
    } else {
      getTrackingAdapter.sendLinkEvent({
        eventName: AI_GENERATE_BUTTON_PRESSED,
      });
    }

    onWriteWithAIClick(language);
  };

  const shouldShowAILanguageSelectionButton = hasOtherLanguageAIButton(site);

  if (shouldShowAILanguageSelectionButton) {
    return (
      <Layout>
        <Text>{t('write-with-AI-cta-section-info-text')}</Text>
        <LanguageSelectionAIContentGenerateButton
          buttonText={t(
            'write-with-AI-cta-section-write-with-ai-button-text',
          ).concat(' ')}
          buttonStyle={styles.writeWithAIButton}
          buttonTextStyle={styles.writeWithAIButtonText}
          generationLanguages={generationLanguages}
          handleOnClick={handleOnClick}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <Text>{t('write-with-AI-cta-section-info-text')}</Text>
      <AIContentGenerateButton
        buttonText={t('write-with-AI-cta-section-write-with-ai-button-text')}
        buttonStyle={styles.writeWithAIButton}
        buttonTextStyle={styles.writeWithAIButtonText}
        handleOnClick={handleOnClick}
      />
    </Layout>
  );
};
